/**
 * Localized<T> — generic per-locale value wrapper.
 *
 * Design goal: zero breakage for legacy data.
 * - DB campaigns created before bilingual support store fields as plain
 *   strings (e.g. `name: "..."`). Backend's post-find hook normalizes
 *   these to `{ he: "..." }` on read, but `resolve()` below is *also*
 *   tolerant of raw strings — defense in depth, in case any code path
 *   bypasses the normalizer.
 */

import type { AppLocale } from './config';

export type Localized<T> = { he: T; en?: T };

export type Localizable<T> = Localized<T> | T | undefined | null;

function isLocalizedObject<T>(v: unknown): v is Localized<T> {
  return (
    !!v &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    ('he' in (v as Record<string, unknown>) || 'en' in (v as Record<string, unknown>))
  );
}

/**
 * Returns the value for the requested locale, falling back to:
 *   1) the other locale, then
 *   2) undefined.
 * If passed a non-Localized value (legacy string, etc.), returns it as-is.
 */
export function resolve<T>(value: Localizable<T>, locale: AppLocale): T | undefined {
  if (value == null) return undefined;
  if (!isLocalizedObject<T>(value)) return value as T;
  return value[locale] ?? value.he ?? value.en;
}

/** Convenience: resolve and provide an empty-string fallback. */
export function resolveStr(value: Localizable<string>, locale: AppLocale): string {
  return resolve<string>(value, locale) ?? '';
}

/**
 * Set or update one locale value on a Localized object without losing the other.
 * Handles legacy raw values by lifting them into { he: <raw> } first.
 */
export function setLocale<T>(
  value: Localizable<T>,
  locale: AppLocale,
  next: T
): Localized<T> {
  let base: Localized<T>;
  if (isLocalizedObject<T>(value)) {
    base = { ...value } as Localized<T>;
  } else if (value == null) {
    base = { he: undefined as unknown as T };
  } else {
    base = { he: value as T };
  }
  base[locale] = next;
  return base;
}

/**
 * Whether the value has explicit content for the given locale.
 * (Used by the builder to show "missing translation" badges.)
 */
export function hasLocale<T>(value: Localizable<T>, locale: AppLocale): boolean {
  if (!isLocalizedObject<T>(value)) return locale === 'he' && value != null && value !== '';
  const v = value[locale];
  return v != null && v !== '';
}
